// The version number a saved deck carries in its file name.
//
// Every deck is written out as `<name>_v<number>.json`, and three separate
// things need to agree about what that number is: the "Save" button (which
// rewrites the current version), the "Save +" button (which starts the next
// one), and the moment a file is opened (which has to work out where the deck
// left off). They used to agree by accident — the version lived only in the
// deck's `exportVersion` field, so a file whose JSON had lost that field came
// back as version 1 and the next "Save +" wrote `_v2.json` over a file that
// already existed.
//
// The rules, in one place:
//
//   * The suffix is the LAST `_v<digits>` in the name, and only when it ends
//     the name. `Week_2_Animation_v10` is version 10, not version 2 — a number
//     that is part of the deck's own name is not a version.
//   * Any number of digits: `_v1`, `_v9`, `_v10`, `_v99`, `_v137`.
//   * Nothing is assumed about the characters around it. Spaces, hyphens and
//     underscores all survive, because instructors name decks like
//     `DSGN1140 - Week 1 - Traditional 2D Animation_v133.json`.
//   * A name with no suffix has no version. That is different from version 1:
//     "never saved" is what lets the first Save create v1 rather than assume
//     one already exists.

/** `_v` followed by digits, at the very end of the name (extension stripped). */
const VERSION_SUFFIX = /_v(\d+)$/i;

/** Trailing extension, e.g. `.json`. */
const EXTENSION = /\.[a-z0-9]+$/i;

/**
 * Pull a deck file name apart.
 *
 * @param {string} fileName - with or without an extension
 * @returns {{ baseName: string, version: number|null, extension: string }}
 *          `version` is null when the name carries no `_v<number>` suffix.
 */
export const parseVersionedFileName = (fileName) => {
  const raw = typeof fileName === 'string' ? fileName.trim() : '';
  if (!raw) return { baseName: '', version: null, extension: '' };

  const extMatch = raw.match(EXTENSION);
  const extension = extMatch ? extMatch[0] : '';
  const stem = extension ? raw.slice(0, -extension.length) : raw;

  const versionMatch = stem.match(VERSION_SUFFIX);
  if (!versionMatch) return { baseName: stem, version: null, extension };

  const version = Number.parseInt(versionMatch[1], 10);
  return {
    baseName: stem.slice(0, -versionMatch[0].length),
    // A suffix of `_v0` is a name, not a version: versions start at 1.
    version: Number.isFinite(version) && version > 0 ? version : null,
    extension
  };
};

/** The version a file name declares, or null when it declares none. */
export const versionFromFileName = (fileName) => parseVersionedFileName(fileName).version;

/** The deck's name with its version suffix removed. */
export const baseNameFromFileName = (fileName) => parseVersionedFileName(fileName).baseName;

/**
 * Build the file name for one version of a deck.
 *
 * The base name is used as given apart from its own version suffix, so calling
 * this with a name that already carries one replaces it rather than stacking a
 * second: `deck_v3` at version 4 is `deck_v4.json`, never `deck_v3_v4.json`.
 */
export const buildVersionedFileName = (baseName, version, extension = '.json') => {
  const stem = baseNameFromFileName(baseName) || 'lesson';
  const number = Number.isFinite(Number(version)) && Number(version) > 0 ? Math.floor(Number(version)) : 1;
  return `${stem}_v${number}${extension}`;
};

/**
 * The version a deck should be treated as sitting at once it is open.
 *
 * The deck's own `exportVersion` wins: it is what the app wrote when it last
 * saved, and it survives a file being renamed. The file name is the fallback,
 * which is what makes a hand-renamed or hand-edited deck open at the right
 * number instead of silently resetting to 1 and overwriting an earlier file on
 * the next "Save +".
 *
 * @returns {number|null} null when neither source knows — a deck that has
 *          never been saved, whose first Save is therefore version 1.
 */
export const resolveOpenedDeckVersion = ({ exportVersion, fileName } = {}) => {
  const declared = Number.parseInt(exportVersion, 10);
  if (Number.isFinite(declared) && declared > 0) return declared;
  return versionFromFileName(fileName);
};

/* ------------------------------------------------------------------ *
 * Which file the deck on screen came from
 *
 * Opening a deck from the launch screen, or from Open ▸ Open file, remounts
 * the editor with the parsed JSON as `initialData` — and the file's NAME is
 * dropped on the way, because only the deck object survives the remount. That
 * is fine until the JSON has no `exportVersion` to read, at which point the
 * only remaining record of where the deck left off is the name that was just
 * thrown away, and `wee_v12.json` reopens as version 1.
 *
 * A module-level note is enough: the open and the remount happen in the same
 * tab, microseconds apart, and nothing else needs to know. It is deliberately
 * NOT part of the deck object — a deck is a file people share, and the name it
 * happened to have on someone else's disk is not part of its content.
 * ------------------------------------------------------------------ */

/**
 * The note is keyed to the deck object itself, not simply "the last file
 * opened". Two reasons, and both bite:
 *
 *   - React's StrictMode mounts, unmounts and remounts a component in
 *     development, so an effect that *consumed* the note would find it gone by
 *     the mount whose state survives, and the version would silently fall back
 *     to 1 in dev only;
 *   - a template chosen ten minutes after a file was opened must not inherit
 *     that file's version, and an identity check refuses it for free.
 */
let openedFrom = { deck: null, fileName: '' };

/** Record the file a deck was just read from, before the editor remounts. */
export const rememberOpenedFileName = (fileName, deck = null) => {
  openedFrom = { deck: deck || null, fileName: typeof fileName === 'string' ? fileName : '' };
};

/**
 * The file this exact deck object was read from, if it was read from one.
 *
 * @param {object} deck - the object handed to the editor as `initialData`
 * @returns {string} the file name, or '' for a deck that came from anywhere
 *          else (a template, a paste, a fresh start)
 */
export const openedFileNameFor = (deck) => (
  deck && openedFrom.deck === deck ? openedFrom.fileName : ''
);

/** Forget the note — for a caller that has replaced the deck outright. */
export const forgetOpenedFileName = () => { openedFrom = { deck: null, fileName: '' }; };

/** The version the next "Save +" writes. */
export const nextVersionNumber = (version) => {
  const current = Number.parseInt(version, 10);
  return Number.isFinite(current) && current > 0 ? current + 1 : 1;
};

/**
 * The version a save should write, given what the deck is sitting at.
 *
 * @param {object} options
 * @param {number|null} options.currentVersion - null for a deck never saved
 * @param {boolean} options.versionUp - true for "Save +"
 * @returns {number}
 *
 * The first Save of a never-saved deck is version 1, not version 2. "Save +"
 * on a deck that has never been saved is also version 1: there is nothing to
 * sit alongside yet, and starting a brand-new deck at v2 has never been what
 * anyone meant by it.
 */
export const versionForSave = ({ currentVersion, versionUp }) => {
  const current = Number.parseInt(currentVersion, 10);
  const hasCurrent = Number.isFinite(current) && current > 0;
  if (!hasCurrent) return 1;
  return versionUp ? current + 1 : current;
};
